// Hideo - settings store: the device preferences (localStorage "creatorOS_settings"),
// the open tile of the Settings page (openSettings: other code opens a section directly, e.g. "change" next to the
// autosave in the Profiles window) and the installable app (the browser's beforeinstallprompt, Settings › App).
import { defineStore } from 'pinia'
import { reactive, ref, shallowRef } from 'vue'
import { SETTINGS_KEY, parseSettings, type DeviceSettings } from '../features/settings'
import { renderDriveUi } from '../features/cloud/drive'
import { cloudSyncNow, renderSyncUi } from '../features/cloud/autosync'
import { app } from '../features/app'

/** The browser's install offer (Chrome, Edge, Android): not in the TypeScript DOM types */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<unknown>
}

function readStored(): DeviceSettings {
  try {
    return parseSettings(localStorage.getItem(SETTINGS_KEY))
  } catch {
    return parseSettings(null)
  }
}

export const useSettingsStore = defineStore('settings', () => {
  /** Device preferences (reactive: the Settings page follows them); change them with set() */
  const values = reactive<DeviceSettings>(readStored())
  function set<K extends keyof DeviceSettings>(k: K, v: DeviceSettings[K]): void {
    values[k] = v
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(values)) } catch { /* storage full or blocked: kept for this session */ }
    if (k === 'autoSaveMin') {
      // Profiles window and sync indicator say whether autosave is on; turning it on saves at once
      renderDriveUi()
      renderSyncUi()
      if (v) void cloudSyncNow()
    }
  }

  /** Open section of the Settings page ('' = none); kept while moving between pages */
  const tab = ref('')
  /** Opens Settings on a section */
  function open(k: string): void {
    tab.value = k
    app.switchTab('settings')
  }

  // ===== Installable app (PWA) =====
  const installPrompt = shallowRef<InstallPromptEvent | null>(null)
  window.addEventListener('beforeinstallprompt', e => { e.preventDefault(); installPrompt.value = e as InstallPromptEvent })
  window.addEventListener('appinstalled', () => { installPrompt.value = null })
  async function install(): Promise<void> {
    const p = installPrompt.value
    if (!p) return
    p.prompt()
    try { await p.userChoice } catch { /* dismissed */ }
    installPrompt.value = null
  }

  return { values, set, tab, open, installPrompt, install }
})

/** Hideo is running as an installed app (standalone window, or added to the iPhone Home Screen) */
export const appInstalled = (): boolean =>
  (!!window.matchMedia && matchMedia('(display-mode: standalone)').matches) || (navigator as Navigator & { standalone?: boolean }).standalone === true
